import { useState, useEffect } from 'react';
import { ShieldAlert, ExternalLink, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OBBBA Disclaimer, surfaces July 1, 2026 federal student loan changes
 * (One Big Beautiful Bill Act). Pulls rule metadata live so the message
 * stays accurate when caps/rates update.
 *
 * Usage:
 *   <OBBBADisclaimer />            // full banner
 *   <OBBBADisclaimer compact />    // small inline note
 *   <OBBBADisclaimer dismissible />// can be dismissed for the session
 */
export default function OBBBADisclaimer({ compact = false, dismissible = false }) {
  const [rules, setRules] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (dismissible && sessionStorage.getItem('obbba_disclaimer_dismissed') === '1') {
      setDismissed(true);
    }
    // Public endpoint, works pre-login on marketing pages too
    axios.get(`${API}/federal-loan-rules/active/public`).then((r) => {
      setRules(r.data?.rules || null);
    }).catch(() => { /* fail silent, fall back to static text */ });
  }, [dismissible]);

  if (dismissed) return null;

  const isOBBBA = rules?.effective_date >= '2026-07-01';
  const headline = isOBBBA
    ? 'Federal student loan rules changed July 1, 2026'
    : 'Federal student loan rules are changing July 1, 2026';

  const body = (
    <>
      Under the <strong>One Big Beautiful Bill Act</strong>, new graduate, professional, and parent borrowers face
      annual + lifetime borrowing caps, and Grad PLUS Loans are no longer available for new borrowers.
      Estimates shown below reflect the rules in effect for your borrower type and program start date.
      <strong> This is not financial or legal advice, confirm specifics with your financial aid office.</strong>
    </>
  );

  const handleDismiss = () => {
    sessionStorage.setItem('obbba_disclaimer_dismissed', '1');
    setDismissed(true);
  };

  if (compact) {
    return (
      <div
        className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs"
        data-testid="obbba-disclaimer-compact"
      >
        <ShieldAlert className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p>
          <strong>OBBBA notice:</strong> {headline.toLowerCase()}. New federal loan caps apply; Grad PLUS no longer available for new borrowers.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-4" data-testid="obbba-disclaimer">
      <div className="flex items-center gap-2 flex-wrap px-3 py-2 rounded-full border border-amber-500/30 bg-amber-500/[0.07]">
        <ShieldAlert className="w-3.5 h-3.5 text-amber-300 shrink-0" />
        <p className="text-xs text-amber-100/90">{headline}</p>
        <button onClick={() => setOpen((v) => !v)} data-testid="obbba-disclaimer-more"
          className="text-xs text-amber-300 hover:text-amber-100 underline">
          {open ? 'Hide' : 'What changed'}
        </button>
        {dismissible && (
          <button onClick={handleDismiss} className="ml-auto text-amber-200/60 hover:text-amber-100"
            aria-label="Dismiss disclaimer" data-testid="obbba-disclaimer-dismiss">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="mt-2 p-3 rounded-xl border border-amber-500/25 bg-amber-500/[0.05]">
          <p className="text-xs text-amber-50/90 leading-relaxed">{body}</p>
          {rules?.effective_date && (
            <p className="text-amber-200/60 text-[11px] mt-2">
              Rule set in effect: <span className="font-mono">{rules.label || rules.effective_date}</span>{' · '}
              <a href="https://studentaid.gov/announcements-events/obbba" target="_blank" rel="noopener noreferrer"
                className="underline hover:text-amber-100">Learn more <ExternalLink className="w-3 h-3 inline" /></a>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
